import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type CSSProperties,
} from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useSmartBack } from "../hooks/smartBack";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Save20Regular,
  CheckmarkCircle20Filled,
  Warning20Regular,
  PeopleSwap20Regular,
  DismissCircle20Regular,
  Edit16Regular,
  ArrowUndo16Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { apiUrl } from "../utils/api";
import {
  mergeCluster,
  discardAltkundenCluster,
  fetchRelatedSummary,
  fetchRelatedDetail,
  updateDuplicateCandidateStatus,
  lockDuplicateCluster,
  unlockDuplicateCluster,
  listDuplicateTeams,
  RelatedSummary,
  RelatedDetail,
  RelatedDetailItem,
  MergeRecordSelections,
} from "../services/duplicateCandidateService";
import { CountChips } from "../components/duplicates/RelatedRecords";
import { useReferenceData } from "../context/ReferenceDataContext";
import { useAuth } from "../context/AuthContext";
import { contactTypeLabel } from "../utils/contactTypeLabel";
import {
  fetchAltkundeStatuses,
  type ContactStatusOption,
} from "../services/altkundenCleanupService";
import { bestaetigen } from "../components/redesign/bestaetigen";

/** Duplikaten-Übersicht (Rücksprungziel nach erfolgreichem Merge / Status-Wechsel).
 *  Absolute Route statt navigate(-1): die Prüfungsseite kann per Deep-Link aus der
 *  Kontaktliste geöffnet werden (kein Übersichts-History-Eintrag). */
const OVERVIEW_ROUTE = "/contacts/duplicates";

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "48px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1280px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  body: {
    backgroundColor: BRAND.white,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "18px",
  },
  sectionLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "8px",
  },
  winnerRow: { display: "flex", columnGap: "12px", rowGap: "12px", flexWrap: "wrap" },
  candidateCard: {
    flexGrow: 1,
    flexBasis: "240px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  candidateActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.ink),
    boxShadow: `0 0 0 3px ${BRAND.purple}33`,
  },
  candidateDeselected: {
    opacity: 0.5,
    backgroundColor: "#f6f4f7",
  },
  includeRow: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.74rem",
    color: BRAND.muted,
    cursor: "pointer",
  },
  winnerName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
  },
  winnerSub: { color: BRAND.muted, fontSize: "0.82rem" },
  boTeamLine: { color: BRAND.ink, fontSize: "0.84rem", fontWeight: 700 },
  // Hinweis an einer Beziehung, die der Firma↔Person-Merge umschreibt. Kurz
  // gehalten: die Vergleichsspalten sind minmax(200px, 1fr) in einem
  // overflow-x-Container, ein langer nowrap-Text erzwänge dort Querscrollen.
  reclassifyBadge: {
    marginLeft: "6px",
    paddingTop: "1px",
    paddingBottom: "1px",
    paddingLeft: "6px",
    paddingRight: "6px",
    ...shorthands.borderRadius("6px"),
    fontSize: "0.72rem",
    fontWeight: 600,
    backgroundColor: "#f3ecf4",
    color: BRAND.purple,
    whiteSpace: "nowrap",
  },
  // Weiß-Varianten für die lila Sieger-Karte. MÜSSEN nach den Klassen stehen,
  // die sie überschreiben (includeRow/winnerName/winnerSub/boTeamLine): Griffel
  // erzeugt atomare Klassen, bei denen für dieselbe Property die SPÄTER
  // definierte gewinnt — nicht die im className zuletzt genannte. Kombiniert
  // wird zusätzlich per mergeClasses, das genau diese Property-Kollision auflöst.
  textWhite: { color: BRAND.white },
  textWhiteSoft: { color: "rgba(255,255,255,0.82)" },
  survivorPick: {
    alignSelf: "flex-start",
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    fontWeight: 600,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: "#f5eef6" },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  survivorPickActive: {
    // On the purple active card, invert: white button with purple text so it pops.
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    ...shorthands.borderColor(BRAND.white),
    ":hover": { backgroundColor: "rgba(255,255,255,0.85)" },
  },
  tableScroll: { overflowX: "auto" },
  table: {
    display: "grid",
    columnGap: "8px",
    rowGap: "6px",
    fontSize: "0.86rem",
    minWidth: "fit-content",
  },
  th: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#5d5664",
    paddingBottom: "6px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  thWinner: { color: BRAND.purple },
  fieldLabel: {
    color: BRAND.ink,
    fontWeight: 600,
    fontSize: "0.82rem",
    display: "flex",
    alignItems: "center",
  },
  cell: {
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("6px"),
    overflowWrap: "break-word",
  },
  cellWinner: {
    backgroundColor: "#f5eef6",
    ...shorthands.border("1px", "solid", BRAND.purple),
  },
  selCell: {
    appearance: "none",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: BRAND.bg,
    ...shorthands.border("1px", "solid", "transparent"),
    ...shorthands.borderRadius("6px"),
    overflowWrap: "break-word",
    ":hover": {
      backgroundColor: "#f5eef6",
      ...shorthands.borderColor(BRAND.borderDashed),
    },
  },
  selCellActive: {
    backgroundColor: "#f5eef6",
    ...shorthands.borderColor(BRAND.purple),
    fontWeight: 700,
    color: BRAND.purple,
  },
  compareGrid: {
    display: "grid",
    columnGap: "8px",
    minWidth: "fit-content",
  },
  compareCol: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: BRAND.bg,
  },
  compareColHead: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.9rem",
    color: BRAND.ink,
    paddingBottom: "4px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    marginBottom: "2px",
  },
  /** Aubergine-/lila Typ-Pille ("FIRMA"/"PERSON") über dem Kandidatennamen. */
  typeBadge: {
    display: "block",
    width: "fit-content",
    marginBottom: "3px",
    paddingTop: "1px",
    paddingBottom: "1px",
    paddingLeft: "7px",
    paddingRight: "7px",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.62rem",
    fontWeight: 800,
    letterSpacing: "0.06em",
    lineHeight: 1.5,
    textTransform: "uppercase",
    ...shorthands.borderRadius("999px"),
  },
  groupLabel: {
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.purple,
    marginTop: "4px",
  },
  recordItem: { fontSize: "0.84rem", color: BRAND.ink },
  itemSub: { color: BRAND.muted },
  recordEmpty: { fontSize: "0.84rem", color: BRAND.muted, fontStyle: "italic" },
  selItemRow: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "6px",
    fontSize: "0.84rem",
    color: BRAND.ink,
  },
  selItemDropped: { opacity: 0.4, textDecorationLine: "line-through" },
  selItemCheckbox: { marginTop: "2px", flexShrink: 0, cursor: "pointer" },
  infoIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "15px",
    height: "15px",
    marginLeft: "6px",
    fontSize: "0.62rem",
    fontWeight: 700,
    color: BRAND.purple,
    backgroundColor: "#f0e6f2",
    ...shorthands.borderRadius("999px"),
    cursor: "help",
  },
  bankNote: {
    fontSize: "0.74rem",
    color: BRAND.muted,
    marginBottom: "6px",
    fontStyle: "italic",
  },
  footerWrap: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  footerRight: { display: "flex", alignItems: "center", columnGap: "10px" },
  footerMsgRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 600,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ":hover": { backgroundColor: BRAND.ink },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  secondaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  hint: { color: BRAND.muted, fontSize: "0.82rem", marginBottom: "8px" },
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  successBox: {
    backgroundColor: "#e6f6ec",
    color: "#0e7c3a",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", "#b6dfc4"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  /** Hervorgehobener Hinweis-Block für reine Altkunden-Cluster (Vorab-Frage). */
  altkundenBox: {
    backgroundColor: "#fbf4ec",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", "#e6c79a"),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  /** Gefährliche (löschende) Aktion: rotbrauner Button für „Cluster verwerfen". */
  dangerBtn: {
    alignSelf: "flex-start",
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "#8b1c1c",
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 600,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", "#8b1c1c"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ":hover": { backgroundColor: "#6f1414" },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
});

interface ContactRich {
  id: number;
  lastname?: string;
  firstName?: string;
  displayName?: string;
  contactNbrInt?: string;
  contactNbrExt?: string;
  birthDate?: string;
  type?: { type?: string } | null;
  /** Rechtsform (FK ContactLegalForm, eager geladen) — Label `legalForm`,
   *  persistiert über die Spalte `legalFormId`. Nur bei Firmen befüllt. */
  legalForm?: { id?: number; legalForm?: string } | null;
  /** Kundenstatus (FK ContactStatus, eager geladen) — im Merge auswählbar. */
  status?: { id?: number; status?: string } | null;
  /** BO responsibility (Kontaktverantwortlicher) — the responsible team id. */
  boTeamId?: string | null;
  defaultAddress?: { street?: string; postalCode?: string; city?: string };
}

interface FieldDef {
  key: string;
  label: string;
  get: (c: ContactRich) => string | undefined;
  /** When true, the user can pick which contact's value the survivor keeps. */
  selectable?: boolean;
  /** DB column to override (must be a gap-fillable scalar). */
  column?: string;
  /** If set, the survivor's value is directly editable (pencil): text/date input or status dropdown. */
  editType?: "text" | "date" | "status";
}

/** Merge-Konstellation des Clusters, aus den Typen der ausgewählten Kandidaten:
 *  alle "Firma" → company, alle "Person" → person, sonst gemischt. */
type ClusterMode = "company" | "person" | "mixed";

/**
 * Beziehungstypen, die eine Rolle IN EINEM UNTERNEHMEN beschreiben. Muss mit
 * ROLE_RELATION_TYPES in apps/api/src/duplicate-candidate/duplicate-candidate.service.ts
 * übereinstimmen — dort entscheidet dieselbe Liste, was beim Firma↔Person-Merge
 * tatsächlich umgeschrieben wird; hier nur, was als Hinweis angezeigt wird.
 */
const ROLE_RELATION_TYPES = [
  "Mensch fürs Unternehmen",
  "Mensch im Unternehmen",
  "Geschäftsführer",
  "Gesellschafter",
  "Mitarbeiter",
];

// Wiederverwendbare Feld-Bausteine, je Modus unterschiedlich zusammengesetzt.
const FIELD_NAME: FieldDef = {
  key: "name",
  label: "Name",
  get: (c) => c.lastname,
  selectable: true,
  column: "lastname",
  editType: "text",
};
const FIELD_FIRSTNAME: FieldDef = {
  key: "firstName",
  label: "Vorname",
  get: (c) => c.firstName,
  selectable: true,
  column: "firstName",
  editType: "text",
};
/** Rechtsform: Anzeige liest das Label (`legalForm.legalForm`), persistiert wird
 *  aber die FK über `column: "legalFormId"`. Daher bewusst PICK-ONLY (kein
 *  editType/Pencil) — ein freier Text würde keiner gültigen legalFormId
 *  entsprechen. Der Pick kopiert legalFormId vom Quell-Kontakt. */
const FIELD_LEGALFORM: FieldDef = {
  key: "legalForm",
  label: "Rechtsform",
  get: (c) => c.legalForm?.legalForm,
  selectable: true,
  column: "legalFormId",
};
const FIELD_BIRTHDATE: FieldDef = {
  key: "birthDate",
  label: "Geburtsdatum",
  get: (c) => (c.birthDate ? String(c.birthDate).slice(0, 10) : undefined),
  selectable: true,
  column: "birthDate",
  editType: "date",
};
const FIELD_CONTACTSTATUS: FieldDef = {
  key: "contactStatus",
  label: "Kontaktstatus",
  get: (c) => c.status?.status,
  selectable: true,
  column: "contactStatusId",
  editType: "status",
};
const FIELD_NBR_INT: FieldDef = {
  key: "nbrInt",
  label: "Interne Nr.",
  get: (c) => c.contactNbrInt,
};
const FIELD_NBR_EXT: FieldDef = {
  key: "nbrExt",
  label: "Allfinanznr.",
  get: (c) => c.contactNbrExt,
};

/** Type-aware comparison fields: a Firma has a company name + Rechtsform (no
 *  Vorname/Geburtsdatum), a Person has Name + Vorname + Geburtsdatum. Ein
 *  gemischtes Cluster zeigt die Vereinigung — leere Zellen rendern als leer. */
const fieldsFor = (mode: ClusterMode): FieldDef[] => {
  switch (mode) {
    case "company":
      return [
        FIELD_NAME,
        FIELD_LEGALFORM,
        FIELD_CONTACTSTATUS,
        FIELD_NBR_INT,
        FIELD_NBR_EXT,
      ];
    case "person":
      return [
        FIELD_NAME,
        FIELD_FIRSTNAME,
        FIELD_BIRTHDATE,
        FIELD_CONTACTSTATUS,
        FIELD_NBR_INT,
        FIELD_NBR_EXT,
      ];
    case "mixed":
      return [
        FIELD_NAME,
        FIELD_FIRSTNAME,
        FIELD_LEGALFORM,
        FIELD_BIRTHDATE,
        FIELD_CONTACTSTATUS,
        FIELD_NBR_INT,
        FIELD_NBR_EXT,
      ];
  }
};

/** Phone/mobile/e-mail buckets, split by privat / geschäftlich. */
const COMM_GROUPS: Array<{
  label: string;
  kind: string;
  category: "privat" | "geschäftlich";
}> = [
  { label: "Telefon privat", kind: "phone", category: "privat" },
  { label: "Telefon geschäftlich", kind: "phone", category: "geschäftlich" },
  { label: "Mobil privat", kind: "mobile", category: "privat" },
  { label: "Mobil geschäftlich", kind: "mobile", category: "geschäftlich" },
  { label: "E-Mail privat", kind: "email", category: "privat" },
  { label: "E-Mail geschäftlich", kind: "email", category: "geschäftlich" },
];
const KNOWN_COMM_KINDS = new Set(["phone", "mobile", "email"]);

type CommValue = { id: number; value: string; isDefault: boolean };

/** Group a contact's communications into labelled (privat/geschäftlich) buckets. */
const groupComms = (
  items: RelatedDetail["communications"],
): Array<{ label: string; values: CommValue[] }> => {
  const out: Array<{ label: string; values: CommValue[] }> = [];
  for (const g of COMM_GROUPS) {
    const values = items
      .filter(
        (it) => it.kind === g.kind && (it.category ?? "privat") === g.category,
      )
      .map((it) => ({
        id: it.id,
        value: it.value ?? it.label,
        isDefault: !!it.isDefault,
      }));
    if (values.length) out.push({ label: g.label, values });
  }
  // Other channels (fax, homepage, social, …) grouped by their raw kind.
  const others = new Map<string, CommValue[]>();
  for (const it of items) {
    if (KNOWN_COMM_KINDS.has(it.kind ?? "")) continue;
    const key = it.kind || "Sonstige";
    if (!others.has(key)) others.set(key, []);
    others
      .get(key)!
      .push({ id: it.id, value: it.value ?? it.label, isDefault: !!it.isDefault });
  }
  for (const [kind, values] of others) out.push({ label: kind, values });
  return out;
};

const nameOf = (c: ContactRich): string =>
  c.displayName ||
  [c.lastname, c.firstName].filter(Boolean).join(", ") ||
  (c.contactNbrInt
    ? `Kunde Nr. ${c.contactNbrInt}`
    : c.contactNbrExt
      ? `Kunde Nr. ${c.contactNbrExt}`
      : `#${c.id}`);

const countTotal = (s?: RelatedSummary): number => {
  if (!s) return 0;
  return (
    s.addresses +
    s.communications +
    s.bankAccounts +
    s.relations +
    s.vorgaenge +
    s.documents +
    s.damages +
    s.contracts +
    s.risks
  );
};

/** Selectable child-record categories (multi-select keep/drop on merge). */
type RecordCat =
  | "addresses"
  | "communications"
  | "banks"
  | "relations"
  | "contracts";

/** The RelatedDetail array backing each selectable category. */
const recordItemsOf = (d: RelatedDetail | undefined, cat: RecordCat): RelatedDetailItem[] => {
  if (!d) return [];
  if (cat === "addresses") return d.addresses;
  if (cat === "communications") return d.communications;
  if (cat === "banks") return d.bankAccounts;
  if (cat === "contracts") return d.contracts;
  return d.relations;
};

const ContactMergePage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  // „Zurück zur Übersicht": history-first (dorthin, wo man herkam), sonst
  // deterministisch die Duplikate-Übersicht (Deep-Link-sicher, replace).
  const goBackToOverview = useSmartBack(OVERVIEW_ROUTE, { replace: true });
  const [params] = useSearchParams();
  // Verwerfen (Soft-Delete) dürfen dieselben Nutzer wie das Löschen in der
  // Kontaktliste: admin/gfgl oder die TEMP-Lösch-Allowlist. Das Backend-Gate
  // bleibt die finale Absicherung; hier nur, damit der Knopf nicht ins Leere läuft.
  const { isAdmin, isGfgl, canTempDeleteContacts } = useAuth();
  const canDiscard = isAdmin || isGfgl || canTempDeleteContacts;

  const memberIds = useMemo<number[]>(() => {
    const clusterIds = params.get("clusterIds");
    if (clusterIds) {
      return clusterIds
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
    }
    const left = Number(params.get("leftId") ?? 0);
    const right = Number(params.get("rightId") ?? 0);
    return [left, right].filter((n) => n > 0);
  }, [params]);

  // Outlook-Importe (Datenbereinigung, User 02.10.2026): `importIds` markiert
  // die Outlook-Kontakte, `survivorId` setzt den bestehenden Kontakt als
  // Sieger vor, `zurueck` führt nach dem Zusammenführen dorthin zurück.
  const importIds = useMemo<Set<number>>(
    () =>
      new Set(
        (params.get("importIds") ?? "")
          .split(",")
          .map((s) => Number(s.trim()))
          .filter((n) => Number.isFinite(n) && n > 0),
      ),
    [params],
  );
  const survivorVorgabe = Number(params.get("survivorId") ?? 0) || null;
  // Nur interne Bereinigungs-Pfade als Rücksprung (kein offener Redirect).
  const rueckRoute = useMemo(() => {
    const z = params.get("zurueck") ?? "";
    return z.startsWith("/datenbereinigung/") ? z : OVERVIEW_ROUTE;
  }, [params]);

  // Pair ids + current status, passed from the overview so we can mark the
  // whole cluster "kein Duplikat" / reopen it from here.
  const pairIds = useMemo<number[]>(() => {
    return String(params.get("pairIds") ?? "")
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  }, [params]);
  const clusterStatus = params.get("status") ?? "open";

  const [contacts, setContacts] = useState<ContactRich[]>([]);
  const [details, setDetails] = useState<Record<number, RelatedDetail>>({});
  const [summary, setSummary] = useState<Record<number, RelatedSummary>>({});
  // BO-team id → name, to show the Kontaktverantwortlicher per cluster member.
  const [teamNames, setTeamNames] = useState<Record<string, string>>({});
  // Raw BO-team options for the assignment <select>.
  const [teamOptions, setTeamOptions] = useState<
    Array<{ id: string; name: string | null }>
  >([]);
  // BO-team assigned to the survivor on merge (Kontaktverantwortlichkeit).
  // Empty = keep the survivor's current BO. Required when the cluster has no
  // single agreed BO and the survivor has none either.
  const [assignBoTeamId, setAssignBoTeamId] = useState<string>("");
  // FO-user assigned to the survivor on merge (Frontoffice). Empty = unchanged.
  const [assignFoUserId, setAssignFoUserId] = useState<string>("");
  // Tenant users for the FO dropdown (only real FO: department='FO', not admin).
  const { users: refUsers } = useReferenceData();
  const [loading, setLoading] = useState(true);
  const [survivorId, setSurvivorId] = useState<number | null>(null);
  // Candidates included in the merge (default: all). Deselected ones are greyed
  // out and excluded from the comparison and the merge.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  // Altkunde-only-Cluster „verwerfen" (Soft-Delete aller Kontakte): läuft.
  const [discarding, setDiscarding] = useState(false);
  // Fehler beim Verwerfen: eigener State, damit die Meldung direkt am Knopf
  // erscheint (die allgemeine errorBox sitzt am Seitenende, außer Sicht).
  const [discardError, setDiscardError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [doneMsg, setDoneMsg] = useState(
    "Zusammenführung erfolgreich. Du kannst diese Seite verlassen.",
  );
  const [lockError, setLockError] = useState<string | null>(null);
  // Per-field overrides: DB column -> chosen source contact id. Empty = default
  // (survivor wins, empty fields gap-filled). Reset when the survivor changes.
  const [picks, setPicks] = useState<Record<string, number>>({});
  // Freie Sieger-Edits je Spalte (DB-Spalte -> Wert; bei Status = ID). Vorrang vor picks.
  // Wird bei Sieger-Wechsel verworfen.
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Kontaktstatus-Optionen für das Status-Dropdown (einmalig geladen).
  const [statusOptions, setStatusOptions] = useState<ContactStatusOption[]>([]);
  // Communications are an EITHER/OR choice per category (label): exactly one
  // value is kept per "Telefon geschäftlich" / "E-Mail privat" / … across the
  // cluster. Maps category label -> chosen communication id (user override).
  const [commPick, setCommPick] = useState<Record<string, number>>({});
  // Record-level DESELECTION per category: ids the user unchecked (default none
  // = keep all). On merge these are dropped from the survivor; an untouched
  // category sends nothing (legacy keep-all).
  const [dropped, setDropped] = useState<Record<RecordCat, Set<number>>>({
    addresses: new Set(),
    communications: new Set(),
    banks: new Set(),
    relations: new Set(),
    // Verträge: standardmäßig sind die Altkunde-Verträge abgewählt (verworfen);
    // wird nach dem Laden der Details gefüllt (siehe unten).
    contracts: new Set(),
  });
  const toggleDropped = (cat: RecordCat, id: number) =>
    setDropped((prev) => {
      const next = new Set(prev[cat]);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, [cat]: next };
    });

  useEffect(() => {
    let aborted = false;
    const run = async () => {
      setLoading(true);
      try {
        const loaded = await Promise.all(
          memberIds.map((id) =>
            fetch(apiUrl(`contact/${id}`)).then((res) =>
              res.ok ? (res.json() as Promise<ContactRich>) : null,
            ),
          ),
        );
        const valid = loaded.filter(Boolean) as ContactRich[];
        const [sums, detailEntries] = await Promise.all([
          fetchRelatedSummary(valid.map((c) => c.id)),
          Promise.all(
            valid.map((c) =>
              fetchRelatedDetail(c.id)
                .then((d) => [c.id, d] as const)
                .catch(() => [c.id, null] as const),
            ),
          ),
        ]);
        if (aborted) return;
        const byId: Record<number, RelatedDetail> = {};
        for (const [id, d] of detailEntries) if (d) byId[id] = d;
        setContacts(valid);
        setSummary(sums);
        setDetails(byId);
        setSelectedIds(new Set(valid.map((c) => c.id)));
        // Altkunde-Verträge standardmäßig abwählen (= verworfen): die eigenen
        // Verträge jedes Teilnehmers mit Status „Altkunde". Übrige Verträge
        // bleiben angewählt und werden automatisch übernommen.
        const altContractIds = new Set<number>();
        for (const c of valid) {
          if (c.status?.status === "Altkunde") {
            for (const it of byId[c.id]?.contracts ?? []) altContractIds.add(it.id);
          }
        }
        setDropped((prev) => ({ ...prev, contracts: altContractIds }));
        // Default survivor: vorgegeben (Outlook-Importe: immer der bestehende
        // Kontakt), sonst ein Kontakt mit Kundennummer, sonst die meisten Datensätze.
        const vorgabe =
          valid.find((c) => c.id === survivorVorgabe) ??
          (importIds.size ? valid.find((c) => !importIds.has(c.id)) : undefined);
        const withNbr = valid.find((c) => c.contactNbrInt && !importIds.has(c.id));
        if (vorgabe) setSurvivorId(vorgabe.id);
        else if (withNbr) setSurvivorId(withNbr.id);
        else {
          let best = valid[0];
          let bestScore = -1;
          for (const c of valid) {
            const s = countTotal(sums[c.id]);
            if (s > bestScore) {
              bestScore = s;
              best = c;
            }
          }
          setSurvivorId(best?.id ?? null);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    run();
    return () => {
      aborted = true;
    };
  }, [memberIds, survivorVorgabe, importIds]);

  // Acquire a soft lock while reviewing this cluster; release on leave.
  useEffect(() => {
    if (memberIds.length === 0) return;
    let active = true;
    setLockError(null);
    (async () => {
      try {
        await lockDuplicateCluster(memberIds);
      } catch (e: any) {
        if (active) setLockError(String(e?.message ?? e));
      }
    })();
    return () => {
      active = false;
      void unlockDuplicateCluster(memberIds);
    };
  }, [memberIds]);

  // If the survivor gets deselected, hand the crown to the first remaining
  // selected candidate (or none) and drop the now-stale field picks.
  useEffect(() => {
    if (survivorId != null && !selectedIds.has(survivorId)) {
      const next = contacts.find((c) => selectedIds.has(c.id));
      setSurvivorId(next?.id ?? null);
      setPicks({});
      setCommPick({});
      setEdits({});
    }
  }, [selectedIds, survivorId, contacts]);

  // Kontaktstatus-Optionen einmalig laden (für das Status-Dropdown beim Editieren).
  useEffect(() => {
    fetchAltkundeStatuses()
      .then(setStatusOptions)
      .catch(() => undefined);
  }, []);

  // Load BO-team names once, to label each cluster member's Verantwortlicher.
  useEffect(() => {
    listDuplicateTeams()
      .then((ts) => {
        const map: Record<string, string> = {};
        for (const t of ts) if (t.name) map[t.id] = t.name;
        setTeamNames(map);
        setTeamOptions(ts);
      })
      .catch(() => undefined);
  }, []);

  const toggleSelected = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onMerge = async () => {
    if (survivorId == null || lockError || !selectedIds.has(survivorId)) return;
    const selected = contacts.filter((c) => selectedIds.has(c.id));
    if (selected.length < 2) return;
    const loserIds = selected.map((c) => c.id).filter((id) => id !== survivorId);
    // Editierte Spalten (freie Sieger-Werte) haben Vorrang: aus den Kandidaten-Picks
    // ausschließen und stattdessen als explizite Werte senden.
    const fieldSelections = Object.entries(picks)
      .filter(
        ([column, id]) =>
          id !== survivorId && selectedIds.has(id) && !(column in edits),
      )
      .map(([column, sourceContactId]) => ({ column, sourceContactId }));
    const explicitValues = Object.entries(edits).map(([column, value]) => ({
      column,
      value,
    }));
    // Record-level DROP-sets: send ONLY the ids the user removed, never an
    // inverse keep-set. A keep-set would make the backend delete every survivor
    // row not listed — but our lists are capped (relatedDetail page cap) and
    // best-effort (a failed detail fetch yields none), so a keep-set could
    // silently hard-delete records the user never saw. A drop-set is additive-
    // safe: anything not explicitly dropped is kept.
    // Addresses / banks / relations: multi-select — drop the unchecked ids.
    const cats: RecordCat[] = ["addresses", "banks", "relations", "contracts"];
    const recordSelections: MergeRecordSelections = {};
    for (const cat of cats) {
      if (dropped[cat].size === 0) continue;
      const ids = new Set(
        selected.flatMap((c) =>
          recordItemsOf(details[c.id], cat).map((it) => it.id),
        ),
      );
      const dropList = [...dropped[cat]].filter((id) => ids.has(id));
      if (dropList.length) recordSelections[cat] = dropList;
    }
    // Communications: EITHER/OR — keep exactly ONE value per label; drop the
    // competing VISIBLE values (explicit drop-set, so unseen / failed-fetch
    // comms are never deleted).
    const commDrop: number[] = [];
    for (const [label, cands] of commCandidatesByLabel) {
      const keepId = effectiveCommPick(label);
      for (const cand of cands) if (cand.id !== keepId) commDrop.push(cand.id);
    }
    if (commDrop.length) recordSelections.communications = commDrop;
    const hasSelections = Object.keys(recordSelections).length > 0;
    setSaving(true);
    setError(null);
    try {
      await mergeCluster(
        survivorId,
        loserIds,
        fieldSelections,
        hasSelections ? recordSelections : undefined,
        assignBoTeamId || undefined,
        assignFoUserId || undefined,
        explicitValues.length ? explicitValues : undefined,
      );
      setDoneMsg(
        "Zusammenführung erfolgreich. Du wirst zur Übersicht weitergeleitet…",
      );
      setDone(true);
      setTimeout(() => navigate(rueckRoute, { replace: true }), 1200);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setSaving(false);
    }
  };

  const onMarkStatus = async (status: "not_a_duplicate" | "open") => {
    if (pairIds.length === 0) return;
    setMarking(true);
    setError(null);
    try {
      await Promise.all(
        pairIds.map((pid) => updateDuplicateCandidateStatus(pid, status)),
      );
      setDoneMsg(
        status === "not_a_duplicate"
          ? 'Als „kein Duplikat" markiert. Weiterleitung zur Übersicht…'
          : "Cluster wieder geöffnet. Weiterleitung zur Übersicht…",
      );
      setDone(true);
      setTimeout(() => navigate(rueckRoute, { replace: true }), 1200);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setMarking(false);
    }
  };

  // Altkunde-only-Cluster verwerfen: alle ausgewählten Kontakte in den
  // Papierkorb (Soft-Delete), zugehörige Kandidaten-Paare → erledigt. Der Server
  // prüft selbst, dass ALLE Kontakte Status „Altkunde" sind (M-SP3a-Endpoint).
  const onDiscardAltkunden = async () => {
    if (lockError || !canDiscard) return;
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (
      !(await bestaetigen({
        titel: "Altkunden verwerfen?",
        text:
          `Dieses Cluster besteht nur aus Altkunden. Alle ${ids.length} Kontakte werden in den ` +
          "Papierkorb gelegt, statt sie zusammenzuführen. Sie bleiben wiederherstellbar.",
        ja: "Verwerfen",
        ton: "danger",
      }))
    )
      return;
    setDiscarding(true);
    setDiscardError(null);
    try {
      await discardAltkundenCluster(ids, "Altkunden-Duplikat");
      setDoneMsg(
        "Altkunden-Cluster verworfen (Papierkorb). Du wirst zur Übersicht weitergeleitet…",
      );
      setDone(true);
      setTimeout(() => navigate(rueckRoute, { replace: true }), 1200);
    } catch (e: any) {
      setDiscardError(String(e?.message ?? e));
    } finally {
      setDiscarding(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.root}>
        <p style={{ color: BRAND.muted }}>Kontakte werden geladen…</p>
      </div>
    );
  }

  if (contacts.length < 2) {
    return (
      <div className={styles.root}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft20Regular /> Zurück
        </button>
        <p>Es konnten nicht genügend Kontakte geladen werden.</p>
      </div>
    );
  }

  const sub = (c: ContactRich) => {
    const parts = [
      c.contactNbrInt ? `Nr. ${c.contactNbrInt}` : "",
      c.defaultAddress?.city
        ? `${c.defaultAddress.postalCode ?? ""} ${c.defaultAddress.city}`.trim()
        : "",
    ].filter(Boolean);
    return parts.join(" · ");
  };

  const selectedContacts = contacts.filter((c) => selectedIds.has(c.id));

  // Communication EITHER/OR selection: exactly one value kept per category
  // (label) across the cluster. Build the candidates per label, then resolve
  // the effective pick: user override → survivor's value → first candidate.
  const commCandidatesByLabel = (() => {
    const map = new Map<string, Array<{ id: number; contactId: number }>>();
    for (const c of selectedContacts) {
      for (const g of groupComms(details[c.id]?.communications ?? [])) {
        const arr = map.get(g.label) ?? [];
        for (const v of g.values) arr.push({ id: v.id, contactId: c.id });
        map.set(g.label, arr);
      }
    }
    return map;
  })();
  const effectiveCommPick = (label: string): number | undefined => {
    const cands = commCandidatesByLabel.get(label) ?? [];
    if (!cands.length) return undefined;
    const picked = commPick[label];
    if (picked != null && cands.some((c) => c.id === picked)) return picked;
    return (cands.find((c) => c.contactId === survivorId) ?? cands[0]).id;
  };
  const gridCols = `160px repeat(${selectedContacts.length}, minmax(160px, 1fr))`;
  const compareCols = `repeat(${selectedContacts.length}, minmax(200px, 1fr))`;
  const survivor = contacts.find((c) => c.id === survivorId) ?? null;
  // Merge-Modus aus den Typen der AUSGEWÄHLTEN Kandidaten: alle Firma → company,
  // alle Person → person, sonst gemischt. Cluster sind i. d. R. typ-homogen
  // (Dedup mischt Firma↔Person nicht), `mixed` ist die defensive Vereinigung.
  const allCompany =
    selectedContacts.length > 0 &&
    selectedContacts.every((c) => c.type?.type === "Firma");
  const allPerson =
    selectedContacts.length > 0 &&
    selectedContacts.every((c) => c.type?.type !== "Firma");
  const clusterMode: ClusterMode = allCompany
    ? "company"
    : allPerson
      ? "person"
      : "mixed";
  // Firma-spezifische Sektionen (z. B. "Menschen fürs Unternehmen") zeigen,
  // sobald mindestens eine Firma im Cluster ist (company oder mixed).
  const isCompany = clusterMode !== "person";
  const fields = fieldsFor(clusterMode);

  // Einordnung einer Beziehung in die beiden Abschnitte. Bewusst am ECHTEN
  // Kontakttyp des Gegenübers: `otherType` wird serverseitig je Spalte relativ
  // berechnet, dieselbe Beziehung landete bei gemischten Clustern deshalb in
  // BEIDEN Abschnitten — und weil `dropped.relations` global nach Id arbeitet,
  // hakte das Abwählen an einer Stelle die andere still mit ab. `otherType` als
  // Rückfallebene, solange eine ältere API-Version noch ohne das Feld antwortet.
  const isCompanyCounterpart = (it: RelatedDetailItem): boolean =>
    it.otherIsCompanyContact ?? it.otherType === "Firma";

  // Beim Firma↔Person-Merge verlieren geerbte Beziehungen ihre Bedeutung: eine
  // Person führt keine „Menschen fürs Unternehmen", eine Firma hat keine
  // Ehepartner. Das Backend schreibt sie um (reclassifyMixedRelations) — hier
  // steht, was passieren wird. Gilt nur für Beziehungen der VERLIERER-Spalten
  // (nur die werden geerbt) und nur mit Personen als Gegenüber.
  const reclassifyBadge = (it: RelatedDetailItem, ownerId: number) => {
    if (clusterMode !== "mixed" || ownerId === survivorId) return null;
    const ownerIsFirma =
      contacts.find((c) => c.id === ownerId)?.type?.type === "Firma";
    const survivorIsFirma = survivor?.type?.type === "Firma";
    if (ownerIsFirma === survivorIsFirma) return null;
    if (isCompanyCounterpart(it)) return null;
    const isRole = ROLE_RELATION_TYPES.includes(it.relationType ?? "");
    if (survivorIsFirma === isRole) return null;
    const short = survivorIsFirma ? "→ Ansprechpartner" : "→ Beziehung";
    const long = survivorIsFirma
      ? "Wird beim Zusammenführen zu „Mensch fürs Unternehmen“ — der Sieger ist eine Firma."
      : "Wird beim Zusammenführen zu einer Beziehung ohne Rolle — der Sieger ist eine Person.";
    return (
      <span title={long} className={styles.reclassifyBadge}>
        {short}
      </span>
    );
  };

  // Reines Altkunden-Cluster: ALLE ausgewählten Kandidaten haben Status
  // „Altkunde" (mind. 1). Dann bieten wir VOR dem Vergleich an, das Cluster zu
  // verwerfen (alle in den Papierkorb) statt es zusammenzuführen — gleiche
  // Altkunde-Prüfung wie bei den Vertrags-Defaults (c.status?.status). Reaktiv,
  // weil die Auswahl live veränderbar ist.
  const isAllAltkunden =
    selectedContacts.length > 0 &&
    selectedContacts.every((c) => c.status?.status === "Altkunde");

  // BO responsibility across the SELECTED cluster members. Show + REQUIRE the
  // assignment selector ONLY when the cluster has no Kontaktverantwortlicher at
  // all — then a BO team must be picked so the merged contact belongs to a team
  // (and leaves the unowned "Alle"/"Ohne Verantwortlichkeit" bucket). A cluster
  // that already has a BO keeps it (the survivor's BO wins); we don't offer a
  // selector for differing BOs here.
  const boTeamIds = Array.from(
    new Set(selectedContacts.map((c) => c.boTeamId ?? null)),
  );
  const allNoBo = boTeamIds.length === 1 && boTeamIds[0] == null;
  const survivorBoTeamId = survivor?.boTeamId ?? null;
  const showBoSelector = allNoBo;
  const boRequired = showBoSelector && !survivorBoTeamId;
  // FO-Auswahl (optional) im selben Fall: User mit FO-Anteil ('FO' oder 'FO+BO'),
  // keine Admins. Admin Mohamed El Jemai (department='FO') erscheint NICHT.
  const foUsers = refUsers.filter((u) => (u.department ?? "").split("+").includes("FO") && u.role !== "admin");

  const canMerge =
    !saving &&
    !discarding &&
    !done &&
    !lockError &&
    survivorId != null &&
    selectedIds.has(survivorId) &&
    selectedContacts.length >= 2 &&
    (!boRequired || !!assignBoTeamId);

  // Typ-Pille ("FIRMA"/"PRIVAT") je Kandidat — pro Spalte, nicht global.
  const typeBadge = (c: ContactRich) =>
    c.type?.type ? (
      <span className={styles.typeBadge}>{contactTypeLabel(c.type.type)}</span>
    ) : null;

  // Gemeinsamer Spaltenkopf: Typ-Pille + Name (+ Sieger-Markierung). An beiden
  // Kopf-Renderpfaden (renderCompare + Feld-Tabelle) verwendet, damit nichts driftet.
  const colHead = (c: ContactRich): ReactNode => (
    <>
      {typeBadge(c)}
      {nameOf(c)}
      {survivorId === c.id ? " · Sieger" : ""}
    </>
  );

  // Render one comparison section: a column per selected contact.
  const renderCompare = (render: (c: ContactRich) => ReactNode) => (
    <div className={styles.tableScroll}>
      <div
        className={styles.compareGrid}
        style={{ gridTemplateColumns: compareCols }}
      >
        {selectedContacts.map((c) => (
          <div key={c.id} className={styles.compareCol}>
            <div
              className={mergeClasses(styles.compareColHead, survivorId === c.id && styles.thWinner)}
            >
              {colHead(c)}
            </div>
            {render(c)}
          </div>
        ))}
      </div>
    </div>
  );

  // A multi-selectable record row (checkbox + content). Checked = kept.
  const selectableItem = (
    cat: RecordCat,
    id: number,
    content: ReactNode,
  ) => {
    const isDropped = dropped[cat].has(id);
    return (
      <label
        key={id}
        className={mergeClasses(styles.selItemRow, isDropped && styles.selItemDropped)}
      >
        <input
          type="checkbox"
          className={styles.selItemCheckbox}
          checked={!isDropped}
          disabled={done}
          onChange={() => toggleDropped(cat, id)}
        />
        <span>{content}</span>
      </label>
    );
  };

  const emptyLine = <div className={styles.recordEmpty}>—</div>;

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={() => navigate(-1)}>
        <ArrowLeft20Regular /> Zurück
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <PeopleSwap20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Kontakte prüfen &amp; zusammenführen</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Wähle den führenden Kontakt (Sieger) und welche Kontakte einbezogen
          werden. Kontakte, die nicht zum Duplikat gehören, kannst du abwählen –
          sie werden ausgegraut und nicht zusammengeführt. Standardmäßig bleiben
          alle Datensätze erhalten; per Häkchen kannst du einzelne Anschriften,
          IBANs, Verknüpfungen (und bei Firmen auch Telefon/E-Mail) abwählen.
          Bei den anklickbaren Feldern wählst du, welcher Wert übernommen wird.
        </span>
      </div>

      <div className={styles.body}>
        {/* Merge-/Status-Meldungen (error/done) erscheinen UNTEN beim Aktions-Button.
            Der Lock-Fehler betrifft das gesamte Review und bleibt prominent oben. */}
        {lockError && (
          <div className={styles.errorBox}>
            <Warning20Regular /> {lockError}
          </div>
        )}

        {/* Candidates: include/exclude + survivor choice */}
        <div>
          <div className={styles.sectionLabel}>Kontakte im Cluster</div>
          <div className={styles.winnerRow}>
            {contacts.map((c) => {
              const isSel = selectedIds.has(c.id);
              const isSurvivor = survivorId === c.id;
              const won = isSurvivor && isSel; // active (purple) card → white text
              return (
                <div
                  key={c.id}
                  className={mergeClasses(
                    styles.candidateCard,
                    won && styles.candidateActive,
                    !isSel && styles.candidateDeselected,
                  )}
                >
                  <label
                    className={mergeClasses(styles.includeRow, won && styles.textWhiteSoft)}
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      disabled={done}
                      onChange={() => toggleSelected(c.id)}
                    />
                    Einbeziehen
                  </label>
                  {importIds.size > 0 && (
                    <span className={mergeClasses(styles.winnerSub, won && styles.textWhiteSoft)} style={{ fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.7rem" }}>
                      {importIds.has(c.id) ? "Outlook-Kontakt" : "Bestehender Kontakt"}
                    </span>
                  )}
                  <span className={mergeClasses(styles.winnerName, won && styles.textWhite)}>
                    {nameOf(c)}
                  </span>
                  <span className={mergeClasses(styles.winnerSub, won && styles.textWhiteSoft)}>
                    {sub(c)}
                  </span>
                  {c.boTeamId && (
                    <span
                      className={mergeClasses(styles.boTeamLine, won && styles.textWhite)}
                    >
                      Verantwortlich (Team):{" "}
                      {teamNames[c.boTeamId] ?? c.boTeamId}
                    </span>
                  )}
                  <CountChips summary={summary[c.id]} onDark={won} />
                  <button
                    type="button"
                    className={mergeClasses(styles.survivorPick, isSurvivor && styles.survivorPickActive)}
                    disabled={done || !isSel}
                    onClick={() => {
                      setSurvivorId(c.id);
                      setPicks({});
                      setEdits({});
                    }}
                  >
                    {isSurvivor ? "★ Sieger" : "Als Sieger wählen"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Kontaktverantwortlichkeit (Backoffice): only shown when the cluster
            has no responsibility — then a BO team MUST be assigned to the
            survivor so the merged contact belongs to a team. */}
        {showBoSelector && (
          <div>
            <div className={styles.sectionLabel}>
              Kontaktverantwortlichkeit (Backoffice)
            </div>
            <div className={styles.hint}>
              Dieser Cluster hat keine Backoffice-Verantwortlichkeit. Bitte ein
              Team für den Sieger auswählen.
            </div>
            <select
              value={assignBoTeamId}
              onChange={(e) => setAssignBoTeamId(e.target.value)}
              disabled={done}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${BRAND.borderDashed}`,
                fontFamily: BRAND.fontPrimary,
                fontSize: "0.9rem",
                background: BRAND.white,
                color: BRAND.ink,
                minWidth: 280,
              }}
            >
              <option value="">
                {survivorBoTeamId
                  ? `Beibehalten: ${teamNames[survivorBoTeamId] ?? survivorBoTeamId}`
                  : "Bitte Team wählen…"}
              </option>
              {teamOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name ?? t.id}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Kontaktverantwortlichkeit (Frontoffice): optional, im selben Fall wie
            der BO-Selector. Nur echte FO-User (department='FO', keine Admins). */}
        {showBoSelector && (
          <div style={{ marginTop: 10 }}>
            <div className={styles.sectionLabel}>
              Kontaktverantwortlichkeit (Frontoffice)
            </div>
            <div className={styles.hint}>
              Optional: zusätzlich einen Frontoffice-Verantwortlichen (FO) für den
              Sieger wählen.
            </div>
            <select
              value={assignFoUserId}
              onChange={(e) => setAssignFoUserId(e.target.value)}
              disabled={done}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${BRAND.borderDashed}`,
                fontFamily: BRAND.fontPrimary,
                fontSize: "0.9rem",
                background: BRAND.white,
                color: BRAND.ink,
                minWidth: 280,
              }}
            >
              <option value="">— FO unverändert —</option>
              {foUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Altkunde-only-Cluster: VOR dem Vergleich anbieten, das Cluster zu
            verwerfen (alle in den Papierkorb) statt es zusammenzuführen. Die
            normale Merge-UI bleibt darunter erreichbar — die Vorab-Frage ist ein
            zusätzlicher Block, kein Ersatz. */}
        {isAllAltkunden && (
          <div className={styles.altkundenBox}>
            <div className={styles.sectionLabel}>Nur Altkunden im Cluster</div>
            <div className={styles.hint}>
              Dieses Cluster besteht nur aus Kontakten mit Status „Altkunde".
              Statt zusammenzuführen kannst du es verwerfen (alle in den
              Papierkorb). Die Kontakte bleiben wiederherstellbar. Alternativ
              kannst du wie gewohnt unten zusammenführen.
            </div>
            <button
              type="button"
              className={styles.dangerBtn}
              disabled={
                discarding ||
                done ||
                !!lockError ||
                selectedIds.size === 0 ||
                !canDiscard
              }
              onClick={onDiscardAltkunden}
            >
              <DismissCircle20Regular />
              {discarding ? "Wird verworfen…" : "Cluster verwerfen (alle löschen)"}
            </button>
            {!canDiscard && (
              <div className={styles.hint}>
                Verwerfen dürfen nur Admin, GF/GL und freigegebene Bereiniger.
                Du kannst das Cluster unten zusammenführen oder die Leitung
                bitten, es zu verwerfen.
              </div>
            )}
            {discardError && (
              <div className={styles.errorBox} role="alert">
                <Warning20Regular /> {discardError}
              </div>
            )}
          </div>
        )}

        {/* Scalar field comparison (with per-field pick) */}
        <div>
          <div className={styles.sectionLabel}>Felder im Vergleich</div>
          <div className={styles.hint}>
            Anklickbare Felder übernehmen den gewählten Wert für den Sieger.
            Interne Nr. / Allfinanznr. folgen dem Sieger.
          </div>
          <div className={styles.tableScroll}>
            <div
              className={styles.table}
              style={{ gridTemplateColumns: gridCols }}
            >
              <span className={styles.th}>Feld</span>
              {selectedContacts.map((c) => (
                <span
                  key={c.id}
                  className={mergeClasses(styles.th, survivorId === c.id && styles.thWinner)}
                >
                  {colHead(c)}
                </span>
              ))}
              {fields.map((f) => {
                const col = f.column;
                const isSel = !!f.selectable && !!col;
                const canEdit = isSel && !!f.editType;
                const dropEdit = (e: Record<string, string>) => {
                  const n = { ...e };
                  delete n[col as string];
                  return n;
                };
                const dropPick = (p: Record<string, number>) => {
                  const n = { ...p };
                  delete n[col as string];
                  return n;
                };
                return (
                  <FieldRow
                    key={f.key}
                    label={f.label}
                    contacts={selectedContacts}
                    survivorId={survivorId}
                    get={f.get}
                    selectable={isSel}
                    selectedId={
                      isSel ? (picks[col as string] ?? survivorId) : survivorId
                    }
                    onPick={
                      isSel
                        ? (id: number) => {
                            setPicks((p) => ({ ...p, [col as string]: id }));
                            setEdits(dropEdit);
                          }
                        : undefined
                    }
                    editType={canEdit ? f.editType : undefined}
                    editValue={canEdit ? edits[col as string] : undefined}
                    statusOptions={
                      f.editType === "status" ? statusOptions : undefined
                    }
                    onEditValue={
                      canEdit
                        ? (v: string) => {
                            setEdits((e) => ({ ...e, [col as string]: v }));
                            setPicks(dropPick);
                          }
                        : undefined
                    }
                    onResetEdit={canEdit ? () => setEdits(dropEdit) : undefined}
                    classes={{
                      fieldLabel: styles.fieldLabel,
                      cell: styles.cell,
                      cellWinner: styles.cellWinner,
                      selCell: styles.selCell,
                      selCellActive: styles.selCellActive,
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Anschriften — multi-select */}
        <div>
          <div className={styles.sectionLabel}>Anschriften</div>
          {renderCompare((c) => {
            const items = details[c.id]?.addresses ?? [];
            if (!items.length) return emptyLine;
            return items.map((it) =>
              selectableItem(
                "addresses",
                it.id,
                <>
                  {it.label}
                  {it.isDefault ? " ★" : ""}
                  {it.sub ? (
                    <span className={styles.itemSub}> — {it.sub}</span>
                  ) : null}
                </>,
              ),
            );
          })}
        </div>

        {/* Kommunikation — ENTWEDER/ODER pro Kategorie: genau EIN Wert je
            „Telefon geschäftlich" / „E-Mail privat" / … wird übernommen (Radio).
            Die Auswahl gilt clusterweit — wählt man den Wert der einen Spalte,
            wird der konkurrierende Wert der anderen Spalte automatisch abgewählt. */}
        <div>
          <div className={styles.sectionLabel}>Kommunikation</div>
          {renderCompare((c) => {
            const groups = groupComms(details[c.id]?.communications ?? []);
            if (!groups.length) return emptyLine;
            return groups.map((g) => (
              <div key={g.label}>
                <div className={styles.groupLabel}>{g.label}</div>
                {g.values.map((v) => {
                  const picked = effectiveCommPick(g.label) === v.id;
                  return (
                    <label
                      key={v.id}
                      className={mergeClasses(styles.selItemRow, !picked && styles.selItemDropped)}
                    >
                      <input
                        type="radio"
                        className={styles.selItemCheckbox}
                        name={`comm-${g.label}`}
                        checked={picked}
                        disabled={done}
                        onChange={() => setCommPick((p) => ({ ...p, [g.label]: v.id }))}
                      />
                      <span>
                        {v.value}
                        {v.isDefault ? " ★" : ""}
                      </span>
                    </label>
                  );
                })}
              </div>
            ));
          })}
        </div>

        {/* Bank / IBANs — multi-select (FK-protected) */}
        <div>
          <div className={styles.sectionLabel}>Bankverbindungen / IBAN</div>
          <div className={styles.bankNote}>
            Von einem Vertrag genutzte IBANs bleiben erhalten, auch wenn
            abgewählt.
          </div>
          {renderCompare((c) => {
            const items = details[c.id]?.bankAccounts ?? [];
            if (!items.length) return emptyLine;
            return items.map((it) =>
              selectableItem(
                "banks",
                it.id,
                <>
                  {it.sub || it.label}
                  {it.isDefault ? " ★" : ""}
                  {it.sub && it.label ? (
                    <span className={styles.itemSub}> — {it.label}</span>
                  ) : null}
                </>,
              ),
            );
          })}
        </div>

        {/* Beziehungen (zu Personen) bzw. „Menschen fürs Unternehmen" (Firma-Merge) — multi-select.
            Ansprechpartner, die selbst in einem offenen Duplikat-Cluster stecken, werden markiert. */}
        <div>
          <div className={styles.sectionLabel}>
            {isCompany ? "Menschen fürs Unternehmen" : "Beziehungen zu Personen"}
            <span
              className={styles.infoIcon}
              title={
                isCompany
                  ? "Ansprechpartner (Menschen fürs Unternehmen) der Firma"
                  : "Beziehungen zu Personen"
              }
            >
              i
            </span>
          </div>
          {renderCompare((c) => {
            const items = (details[c.id]?.relations ?? []).filter(
              (it) => !isCompanyCounterpart(it),
            );
            if (!items.length) return emptyLine;
            return items.map((it) =>
              selectableItem(
                "relations",
                it.id,
                <>
                  {it.label}
                  {it.sub ? (
                    <span className={styles.itemSub}> — {it.sub}</span>
                  ) : null}
                  {reclassifyBadge(it, c.id)}
                  {it.inDuplicateCluster ? (
                    <span
                      title="Dieser Kontakt steckt selbst in einem Duplikat-Cluster — nach dem Zusammenführen dort bereinigen."
                      style={{
                        marginLeft: 6,
                        padding: "1px 6px",
                        borderRadius: 6,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        background: "#fde8e6",
                        color: "#b3261e",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ⚠ mögliche Dublette
                      {it.otherContactNbr ? ` · ${it.otherContactNbr}` : ""}
                    </span>
                  ) : null}
                </>,
              ),
            );
          })}
        </div>

        {/* Verbindungen (zu Firmen) — multi-select */}
        <div>
          <div className={styles.sectionLabel}>
            Verbindungen zu Firmen
            <span className={styles.infoIcon} title="Verbindungen zu Firmen">
              i
            </span>
          </div>
          {renderCompare((c) => {
            const items = (details[c.id]?.relations ?? []).filter((it) =>
              isCompanyCounterpart(it),
            );
            if (!items.length) return emptyLine;
            return items.map((it) =>
              selectableItem(
                "relations",
                it.id,
                <>
                  {it.label}
                  {it.sub ? (
                    <span className={styles.itemSub}> — {it.sub}</span>
                  ) : null}
                </>,
              ),
            );
          })}
        </div>

        {/* Kundenverbindungen — single-select (1 Kontakt = 1 Gruppe) */}
        <div>
          <div className={styles.sectionLabel}>Kundenverbindungen</div>
          {renderCompare((c) => {
            const items = details[c.id]?.kundenverbindungen ?? [];
            const chosen = (picks["contactGroupId"] ?? survivorId) === c.id;
            return (
              <label className={styles.selItemRow}>
                <input
                  type="radio"
                  name="kv-group"
                  className={styles.selItemCheckbox}
                  checked={chosen}
                  disabled={done}
                  onChange={() =>
                    setPicks((p) => ({ ...p, contactGroupId: c.id }))
                  }
                />
                <span>
                  {items.length
                    ? items.map((it) => it.label).join(", ")
                    : "(keine)"}
                </span>
              </label>
            );
          })}
        </div>

        {/* Verträge — Altkunde-Verträge einzeln anwählbar (Default abgewählt =
            verworfen); übrige Verträge werden automatisch übernommen. */}
        <div>
          <div className={styles.sectionLabel}>Verträge</div>
          <div className={styles.hint}>
            Verträge von Teilnehmern mit Status „Altkunde" sind standardmäßig{" "}
            <b>abgewählt</b> (werden verworfen, Soft-Delete) und können einzeln zum
            Übernehmen angehakt werden. Alle übrigen Verträge werden automatisch
            übernommen.
          </div>
          {renderCompare((c) => {
            const items = details[c.id]?.contracts ?? [];
            if (!items.length) return emptyLine;
            const isAltkunde = c.status?.status === "Altkunde";
            return items.map((it) => {
              const content = (
                <>
                  {it.label}
                  {it.sub ? (
                    <span className={styles.itemSub}> — {it.sub}</span>
                  ) : null}
                </>
              );
              return isAltkunde ? (
                selectableItem("contracts", it.id, content)
              ) : (
                <div key={it.id} className={styles.recordItem}>
                  {content}
                </div>
              );
            });
          })}
        </div>
      </div>

      <div className={styles.footerWrap}>
        <div className={styles.footer}>
          {/* „Abbrechen" führt deterministisch zur Übersicht (Deep-Link-sicher). */}
          <button
            className={styles.secondaryBtn}
            onClick={() => navigate(OVERVIEW_ROUTE)}
          >
            <ArrowLeft20Regular /> Abbrechen
          </button>
          <div className={styles.footerRight}>
            {pairIds.length > 0 &&
              (clusterStatus === "not_a_duplicate" ? (
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  disabled={marking || done}
                  onClick={() => onMarkStatus("open")}
                >
                  <CheckmarkCircle20Filled /> Wieder öffnen
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  disabled={marking || done}
                  onClick={() => onMarkStatus("not_a_duplicate")}
                >
                  <DismissCircle20Regular /> Kein Duplikat
                </button>
              ))}
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={!canMerge}
              onClick={onMerge}
            >
              <Save20Regular />
              {saving
                ? "Zusammenführen…"
                : `Jetzt zusammenführen (${Math.max(selectedContacts.length - 1, 0)} → Sieger)`}
            </button>
          </div>
        </div>
        {/* Erfolgs-/Fehlermeldung direkt UNTER der Button-Reihe. */}
        {(error || done) && (
          <div className={styles.footerMsgRow}>
            {error && (
              <div className={styles.errorBox}>
                <Warning20Regular /> {error}
              </div>
            )}
            {done && (
              <>
                <div className={styles.successBox}>
                  <CheckmarkCircle20Filled /> {doneMsg}
                </div>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={goBackToOverview}
                >
                  <ArrowLeft20Regular /> Zurück zur Übersicht
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const FieldRow = ({
  label,
  contacts,
  survivorId,
  get,
  selectable,
  selectedId,
  onPick,
  classes,
  editType,
  editValue,
  statusOptions,
  onEditValue,
  onResetEdit,
}: {
  label: string;
  contacts: ContactRich[];
  survivorId: number | null;
  get: (c: ContactRich) => string | undefined;
  selectable: boolean;
  selectedId: number | null;
  onPick?: (id: number) => void;
  classes: {
    fieldLabel: string;
    cell: string;
    cellWinner: string;
    selCell: string;
    selCellActive: string;
  };
  editType?: "text" | "date" | "status";
  editValue?: string;
  statusOptions?: ContactStatusOption[];
  onEditValue?: (v: string) => void;
  onResetEdit?: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const edited = editValue !== undefined;
  const iconBtn: CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 2,
    display: "inline-flex",
    alignItems: "center",
    color: "inherit",
  };
  const editDisplay =
    editType === "status"
      ? (statusOptions?.find((s) => String(s.id) === String(editValue))
          ?.status ?? editValue)
      : editValue;
  return (
    <>
      <span className={classes.fieldLabel}>{label}</span>
      {contacts.map((c) => {
        const isWinner = c.id === survivorId;
        // Editierbare Sieger-Zelle: Stift → Inline-Input (Text/Datum/Status), Reset.
        if (selectable && editType && isWinner) {
          if (editing) {
            const cur = editValue ?? get(c) ?? "";
            return (
              <span
                key={c.id}
                className={classes.selCell}
                style={{ display: "flex", gap: 4, alignItems: "center" }}
              >
                {editType === "status" ? (
                  <select
                    autoFocus
                    value={cur}
                    onChange={(e) => onEditValue?.(e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <option value="">— Status —</option>
                    {(statusOptions ?? []).map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.status}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    autoFocus
                    type={editType === "date" ? "date" : "text"}
                    value={cur}
                    onChange={(e) => onEditValue?.(e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                )}
                <button
                  type="button"
                  style={iconBtn}
                  title="Fertig"
                  onClick={() => setEditing(false)}
                >
                  ✓
                </button>
              </span>
            );
          }
          return (
            <span
              key={c.id}
              className={mergeClasses(classes.selCell, !!(edited || selectedId === c.id) && classes.selCellActive)}
              style={{
                display: "flex",
                gap: 4,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Wert klickbar: Sieger-Wert übernehmen (verwirft einen Kandidaten-
                  Pick bzw. Edit) — damit man nach dem Picken eines anderen
                  Duplikats wieder den Sieger wählen kann. Stift/Reset separat. */}
              <span
                onClick={() => onPick?.(c.id)}
                title="Wert des Siegers übernehmen"
                style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
              >
                {(edited ? editDisplay : get(c)) ?? "—"}
              </span>
              <span style={{ display: "inline-flex", gap: 2 }}>
                <button
                  type="button"
                  style={iconBtn}
                  title="Sieger-Wert bearbeiten"
                  onClick={() => setEditing(true)}
                >
                  <Edit16Regular />
                </button>
                {(edited || selectedId !== c.id) && (
                  <button
                    type="button"
                    style={iconBtn}
                    title="Auf Sieger-Wert zurücksetzen"
                    onClick={() => {
                      onResetEdit?.();
                      onPick?.(c.id);
                      setEditing(false);
                    }}
                  >
                    <ArrowUndo16Regular />
                  </button>
                )}
              </span>
            </span>
          );
        }
        // Kandidaten-Spalte: Wert übernehmen (Pick) bzw. reine Anzeige.
        return selectable ? (
          <button
            type="button"
            key={c.id}
            className={mergeClasses(classes.selCell, !edited && selectedId === c.id && classes.selCellActive)}
            onClick={() => onPick?.(c.id)}
            title="Diesen Wert für den Sieger übernehmen"
          >
            {get(c) ?? "—"}
          </button>
        ) : (
          <span
            key={c.id}
            className={mergeClasses(classes.cell, survivorId === c.id && classes.cellWinner)}
          >
            {get(c) ?? "—"}
          </span>
        );
      })}
    </>
  );
};

export default ContactMergePage;
